import { useMemo, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { CustomSelect } from 'src/components/custom-select'
import { SelectWrapper } from 'src/components/select-wrapper'
import { ErrorsList } from 'src/components/errors'
import { Spinner } from 'src/components/spinner'
import { exportTemplatesApi, ExportTemplate } from 'src/services/export-templates'
import {
  ExportTemplateForm,
  ExportTemplateFormInputs,
  TExportTemplateForm,
} from 'src/services/export-templates/forms'
import { exportTemplateQueries } from 'src/services/export-templates/queries'
import { organizationQueries } from 'src/services/user'
import { InfoHint } from 'src/components/info-hint'

/**
 * Available fields for export templates, grouped by source.
 * Mirrors COLUMN_CONFIG_MAP from the backend.
 */
const AVAILABLE_FIELDS: { source: string; field: string; label: string }[] = [
  // Report fields
  { source: 'report', field: 'name', label: 'Report Name' },
  { source: 'report', field: 'notes', label: 'Report Notes' },
  { source: 'report', field: 'is_submitted', label: 'Report Submitted' },
  { source: 'report', field: 'is_approved', label: 'Report Approved' },
  // Expense fields
  { source: 'expense', field: 'id', label: 'ID' },
  { source: 'expense', field: 'name', label: 'Name' },
  { source: 'expense', field: 'organization', label: 'Organization' },
  { source: 'expense', field: 'expense_date', label: 'Expense Date' },
  { source: 'expense', field: 'provided_id', label: 'Provided ID' },
  { source: 'expense', field: 'amount', label: 'Amount' },
  { source: 'expense', field: 'amount_currency', label: 'Currency' },
  { source: 'expense', field: 'notes', label: 'Notes' },
  { source: 'expense', field: 'reimburse_to', label: 'Reimburse To' },
  { source: 'expense', field: 'category', label: 'Category' },
  { source: 'expense', field: 'transaction_type', label: 'Transaction Type' },
  { source: 'expense', field: 'cash_flow_type', label: 'Cash Flow Type' },
  { source: 'expense', field: 'is_reimbursable', label: 'Reimbursable' },
  { source: 'expense', field: 'is_reimbursed', label: 'Reimbursed' },
  { source: 'expense', field: 'is_reconciled', label: 'Reconciled' },
  { source: 'expense', field: 'tax_year', label: 'Tax Year' },
  { source: 'expense', field: 'rut', label: 'RUT' },
  { source: 'expense', field: 'identification_number', label: 'ID Number' },
  { source: 'expense', field: 'vendor', label: 'Vendor' },
  { source: 'expense', field: 'payment_account', label: 'Payment Account' },
  // Receipt fields
  { source: 'receipt', field: 'receipts', label: 'Receipt URLs' },
  { source: 'receipt', field: 'receipt_names', label: 'Receipt Names' },
]

const FIELD_GROUPS = [
  { source: 'report', titleKey: 'exportTemplates.reportFields' },
  { source: 'expense', titleKey: 'exportTemplates.expenseFields' },
  { source: 'receipt', titleKey: 'exportTemplates.receiptFields' },
]

const ExportTemplateDetailPageInner = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { createFormFieldChangeHandler, form, overrideForm } = useTnForm<TExportTemplateForm>()

  const { data: template, isLoading: loadingTemplate } = useQuery({
    ...exportTemplateQueries.retrieve(id ?? ''),
    enabled: Boolean(id),
  })

  const { data: organizations, isLoading: loadingOrgs } = useQuery(organizationQueries.list())

  const orgOptions = useMemo(
    () =>
      organizations?.results?.map((org) => ({
        label: org.name,
        value: org.id,
      })) || [],
    [organizations],
  )

  // Populate form when editing existing template
  useEffect(() => {
    if (template && id) {
      const orgRef = template.organizationRef
        ? { label: template.organizationRef.name, value: template.organization }
        : undefined

      const columnKeys = template.columnConfig?.length
        ? template.columnConfig.map((c) => `${c.source}:${c.field}`)
        : []

      overrideForm(
        ExportTemplateForm.create({
          _name: template.name,
          organization: orgRef,
          columnConfig: columnKeys,
        }) as TExportTemplateForm,
      )
    }
  }, [template, id, overrideForm])

  const { mutate: createTemplate, isPending: isCreating } = useMutation({
    mutationFn: (data: { name: string; organization: string; columnConfig: any[] }) =>
      exportTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exportTemplateQueries.all() })
      navigate('/export-templates')
    },
  })

  const { mutate: updateTemplate, isPending: isUpdating } = useMutation({
    mutationFn: (data: Partial<ExportTemplate> & { id: string }) => exportTemplatesApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exportTemplateQueries.all() })
      navigate('/export-templates')
    },
  })

  const isSaving = isCreating || isUpdating
  const selectedFields = new Set(form.columnConfig.value || [])

  const toggleField = (key: string) => {
    const current = form.columnConfig.value || []
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    createFormFieldChangeHandler(form.columnConfig)(next)
  }

  const toggleGroup = (groupSource: string, allSelected: boolean) => {
    const fields = AVAILABLE_FIELDS.filter((f) => f.source === groupSource)
    const fieldKeys = fields.map((f) => `${f.source}:${f.field}`)
    const current = form.columnConfig.value || []

    let next: string[]
    if (allSelected) {
      next = current.filter((k) => !fieldKeys.includes(k))
    } else {
      const toAdd = fieldKeys.filter((k) => !current.includes(k))
      next = [...current, ...toAdd]
    }
    createFormFieldChangeHandler(form.columnConfig)(next)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) return

    const formData = form.val as { name: string; organization: string; columnConfig: any[] }

    if (id) {
      updateTemplate({ id, ...formData })
    } else {
      createTemplate(formData)
    }
  }

  if (id && loadingTemplate) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-bold text-primary-600">
        {id ? t('exportTemplates.editTitle') : t('exportTemplates.createTitle')}
      </h2>

      <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
        <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
          {t('exportTemplates.templateDetails')}
        </h3>
        <div className="space-y-4">
          <div>
            <Input
              label={form._name.label}
              placeholder={form._name.placeholder}
              value={form._name.value ?? ''}
              onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
              onBlur={() => form._name.validate()}
              disabled={isSaving}
              data-testid="template-name-input"
            />
            <ErrorsList errors={form._name.errors} />
          </div>

          <SelectWrapper
            label={form.organization.label}
            onBlur={() => form.organization.validate()}
            data-testid="template-org-select-wrapper"
          >
            <CustomSelect
              searchable
              options={orgOptions}
              values={form.organization.value ? [form.organization.value] : []}
              loading={loadingOrgs}
              disabled={isSaving || Boolean(id)}
              onChange={(values) => {
                if (values.length > 0) {
                  createFormFieldChangeHandler(form.organization)(values[0])
                } else {
                  createFormFieldChangeHandler(form.organization)(undefined)
                }
              }}
            />
            <ErrorsList errors={form.organization.errors} />
          </SelectWrapper>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
        <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
          {t('exportTemplates.columnConfig')}
          <InfoHint text={t('exportTemplates.columnConfigHint')} className="ml-1.5" />
        </h3>

        {FIELD_GROUPS.map((group) => {
          const fields = AVAILABLE_FIELDS.filter((f) => f.source === group.source)
          const allSelected = fields.every((f) => selectedFields.has(`${f.source}:${f.field}`))

          return (
            <div key={group.source} className="mb-6" data-testid={`field-group-${group.source}`}>
              <div className="mb-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => toggleGroup(group.source, allSelected)}
                  className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                  data-testid={`select-all-${group.source}`}
                />
                <h4 className="font-semibold text-gray-700">{t(group.titleKey)}</h4>
              </div>
              <div className="ml-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {fields.map((field) => {
                  const key = `${field.source}:${field.field}`
                  return (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-surface-hover"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFields.has(key)}
                        onChange={() => toggleField(key)}
                        className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                        data-testid={`field-checkbox-${key}`}
                      />
                      <span className="text-sm text-gray-700">
                        {t(`exportTemplates.field_${field.source}_${field.field}`, {
                          defaultValue: field.label,
                        })}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}

        <p className="text-xs text-text-tertiary">
          {selectedFields.size > 0
            ? selectedFields.size !== 1
              ? t('exportTemplates.fieldsSelectedPlural', { count: selectedFields.size })
              : t('exportTemplates.fieldsSelected', { count: selectedFields.size })
            : t('exportTemplates.noFieldsSelected')}
        </p>
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/export-templates')}
          disabled={isSaving}
          data-testid="template-cancel-button"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSaving}
          data-testid="template-save-button"
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export const ExportTemplateDetailPage = () => {
  const { id } = useParams<{ id?: string }>()

  return (
    <FormProvider<ExportTemplateFormInputs> formClass={ExportTemplateForm} key={id}>
      <ExportTemplateDetailPageInner />
    </FormProvider>
  )
}
